import { eventTracker } from 'app/modules/Segment';
import {
  TProperties,
  TContext,
  GroupIdEnum,
  OwnerEnum,
  FeatureEnum,
} from 'app/modules/Segment/types';
import { snakeCaseToTitleCase } from 'app/modules/Segment/utils';

export const EVENTS = {
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_SIGN_UP_ATTEMPT: 'checkout_sign_up_attempt',
  CHECKOUT_STEP_2_ADVANCED: 'checkout_step_2_advanced',
  CHECKOUT_STEP_2_ADVANCED_FAILED: 'checkout_step_2_advanced_failed',
  CHECKOUT_CARD_TOKENIZATION_SUCCESS: 'checkout_card_tokenization_success',
  CHECKOUT_CARD_TOKENIZATION_ERROR: 'checkout_card_tokenization_error',
  CHECKOUT_PAYMENT_FAILED: 'checkout_payment_failed',
  CHECKOUT_PIX_PAYMENT_CLICKED: 'checkout_pix_payment_clicked',
  CHECKOUT_PIX_PAYMENT_FAILED: 'checkout_pix_payment_failed',
  CHECKOUT_PIX_PAYMENT_PAID: 'checkout_pix_payment_paid',
  CHECKOUT_TRY_AGAIN_CLICKED: 'checkout_try_again_clicked',
  CHECKOUT_ACCESS_PLATFORM_CLICKED: 'checkout_access_platform_clicked',
  CHECKOUT_SIGN_UP_ACCOUNT_CREATED: 'sign_up_account_created',
  CHECKOUT_SIGN_UP_GENERAL_INFO_STEP_ADVANCED:
    'sign_up_general_info_step_advanced',
  CHECKOUT_SIGN_UP_ACCOUNT_CREATION_FAILED: 'sign_up_account_creation_failed',
  CAPICANDIA_ONBOARDING_ANSWERED: 'capicandia_onboarding_answered',
  CAPICANDIA_ONBOARDING_STATUS_CHECKED: 'capicandia_onboarding_status_checked',
  CAPICANDIA_ONBOARDING_PLAN_SELECTED: 'capicandia_onboarding_plan_selected',
  CAPICANDIA_DOWNSELL_REQUESTED: 'capicandia_downsell_requested',
} as const;

const GENERAL_LIBRARY_NAME = 'general-library';
const ONBOARDING_ACTIVATION_CATEGORY = 'onboarding_activation';
const OTHER_CATEGORY = 'other';
const PAYMENT_FAILURE_CATEGORY = 'payment_failure';
const PAYMENT_FAILURE_FALLBACK_CATEGORY = 'payment_failure_fallback';
const ERROR_TECHNICAL_CATEGORY = 'error_technical';

/**
 * Subset que alimenta o filtro de segmentação do HubSpot (`filtro_hubspot: 1`).
 * A key não existe no payload dos demais eventos deste arquivo.
 */
const HUBSPOT_FILTER_EVENTS = new Set<string>([
  EVENTS.CHECKOUT_STARTED,
  EVENTS.CHECKOUT_STEP_2_ADVANCED,
  EVENTS.CHECKOUT_STEP_2_ADVANCED_FAILED,
  EVENTS.CHECKOUT_PAYMENT_FAILED,
  EVENTS.CHECKOUT_SIGN_UP_ACCOUNT_CREATED,
  EVENTS.CHECKOUT_SIGN_UP_GENERAL_INFO_STEP_ADVANCED,
]);

type AdditionalProperties = Record<string, unknown>;

type TrackEventParams = {
  event: string;
  category: string;
  eventMeaning: string;
  relevantUserActivity: boolean;
  componentName: string;
  additionalProperties?: AdditionalProperties;
  useTitleCaseEventText?: boolean;
  eventText?: string;
};

const createContext = (componentName: string): TContext => ({
  groupId: GroupIdEnum.GENERAL_GROUP,
  library: {
    name: GENERAL_LIBRARY_NAME,
    componente: componentName,
  },
});

const buildProperties = ({
  event,
  category,
  eventMeaning,
  relevantUserActivity,
  additionalProperties,
  useTitleCaseEventText = false,
  eventText,
}: Omit<TrackEventParams, 'componentName'>): TProperties => ({
  ...(additionalProperties ?? {}),
  owner: OwnerEnum.PAGAMENTOS,
  ...(HUBSPOT_FILTER_EVENTS.has(event) ? { filtro_hubspot: 1 } : {}),
  feature: FeatureEnum.CHECKOUT,
  category,
  event_meaning: eventMeaning,
  relevant_user_activity: relevantUserActivity,
  event_text:
    eventText ??
    (useTitleCaseEventText ? snakeCaseToTitleCase(event) : event),
});

const trackEvent = ({
  event,
  category,
  eventMeaning,
  relevantUserActivity,
  componentName,
  additionalProperties,
  useTitleCaseEventText = false,
  eventText,
}: TrackEventParams): void => {
  eventTracker.track({
    event,
    properties: buildProperties({
      event,
      category,
      eventMeaning,
      relevantUserActivity,
      additionalProperties,
      useTitleCaseEventText,
      eventText,
    }),
    context: createContext(componentName),
  });
};

export function trackCheckoutStarted(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_STARTED,
    category: OTHER_CATEGORY,
    eventMeaning:
      "[Checkout] Step 1 do checkout iniciado. Obs: nos funis de checkout antigos já construídos, para esse mesmo evento é usado o app_jusfy.pages WHERE path LIKE '%register%' para medir o acesso ao step 1 do checkout. A tabela checkout_started é menos atualizada.",
    relevantUserActivity: false,
    componentName: 'checkout-started-component',
    additionalProperties,
  });
}

export function trackCheckoutStep2Advanced(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_STEP_2_ADVANCED,
    category: OTHER_CATEGORY,
    eventMeaning:
      '[Checkout] Step 2 do checkout iniciado. Obs: nos funis de checkout antigos já construídos, para esse mesmo evento é usado o sign_up_general_info_step_advanced para medir o acesso ao step 2 do checkout. A tabela checkout_step_2_advanced é menos atualizada.',
    relevantUserActivity: false,
    componentName: '2-advanced-component',
    additionalProperties,
  });
}

export function trackCheckoutCardTokenizationSuccess(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_CARD_TOKENIZATION_SUCCESS,
    category: OTHER_CATEGORY,
    eventMeaning:
      '[Tokenização de cartão] Retorno da api com sucesso na tokenização de cartão após o clique em realizar cadastro no step 2 do checkout.',
    relevantUserActivity: false,
    componentName: 'card-component',
    additionalProperties,
  });
}

export function trackCheckoutCardTokenizationError(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_CARD_TOKENIZATION_ERROR,
    category: ERROR_TECHNICAL_CATEGORY,
    eventMeaning:
      '[Tokenização de cartão] Retorno da api com erro na tokenização de cartão após o clique em realizar cadastro no step 2 do checkout.',
    relevantUserActivity: false,
    componentName: 'card-component',
    additionalProperties,
  });
}

export function trackCheckoutPaymentFailed(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_PAYMENT_FAILED,
    category: PAYMENT_FAILURE_CATEGORY,
    eventMeaning: '[Checkout] Falha ao concluir pagamento',
    relevantUserActivity: false,
    componentName: 'payment-failed-component',
    additionalProperties,
  });
}

export function trackCheckoutPixPaymentClicked(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_PIX_PAYMENT_CLICKED,
    category: PAYMENT_FAILURE_FALLBACK_CATEGORY,
    eventMeaning:
      '[Checkout - recuperação venda Pix] Clique no botão pagar com Pix, após falha no pagamento por cartão de crédito',
    relevantUserActivity: false,
    componentName: 'button-component',
    additionalProperties,
  });
}

export function trackCheckoutPixPaymentFailed(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_PIX_PAYMENT_FAILED,
    category: PAYMENT_FAILURE_FALLBACK_CATEGORY,
    eventMeaning:
      '[Checkout - recuperação venda Pix] Falha na geração do Pix, após clique no botão pagar por Pix',
    relevantUserActivity: false,
    componentName: 'payment-failed-component',
    additionalProperties,
  });
}

export function trackCheckoutPixPaymentPaid(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_PIX_PAYMENT_PAID,
    category: OTHER_CATEGORY,
    eventMeaning:
      '[Checkout - recuperação venda Pix] Sucesso na geração do Pix, após clique no botão pagar com Pix',
    relevantUserActivity: false,
    componentName: 'payment-paid-component',
    additionalProperties,
  });
}

export function trackCheckoutTryAgainClicked(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_TRY_AGAIN_CLICKED,
    category: PAYMENT_FAILURE_CATEGORY,
    eventMeaning:
      '[Checkout - recuperação venda Pix] Clique no botão Tentar novamente, após falha no pagamento com cartão de crédito',
    relevantUserActivity: false,
    componentName: 'button-component',
    additionalProperties,
  });
}

export function trackCheckoutAccessPlatformClicked(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_ACCESS_PLATFORM_CLICKED,
    category: OTHER_CATEGORY,
    eventMeaning:
      '[Checkout - recuperação venda Pix] Clique no botão Acessar plataforma no modal para pagamento com Pix',
    relevantUserActivity: false,
    componentName: 'button-component',
    additionalProperties,
  });
}

export function trackCheckoutStep2AdvancedFailed(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_STEP_2_ADVANCED_FAILED,
    category: ERROR_TECHNICAL_CATEGORY,
    eventMeaning: '[Checkout] falha ao avançar para o Step 2 do checkout.',
    relevantUserActivity: false,
    componentName: 'advanced-failed-component',
    additionalProperties,
  });
}

export function trackSignUpAccountCreated(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_SIGN_UP_ACCOUNT_CREATED,
    category: ONBOARDING_ACTIVATION_CATEGORY,
    eventMeaning: '[Checkout] Sucesso ao concluir pagamento',
    relevantUserActivity: true,
    componentName: 'account-created-component',
    additionalProperties,
    useTitleCaseEventText: true,
  });
}

export function trackCheckoutSignUpAttempt(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_SIGN_UP_ATTEMPT,
    category: ONBOARDING_ACTIVATION_CATEGORY,
    eventMeaning:
      '[Checkout] Mesmo evento do sign_up_account_creation_submit (este último está mais atualizado e possui um maior período de eventos sendo trackeados)',
    relevantUserActivity: true,
    componentName: 'up-attempt-component',
    additionalProperties,
  });
}

export function trackSignUpGeneralInfoStepAdvanced(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_SIGN_UP_GENERAL_INFO_STEP_ADVANCED,
    category: ONBOARDING_ACTIVATION_CATEGORY,
    eventMeaning:
      '[Checkout] Step 2 do checkout iniciado. Obs: nos funis de checkout antigos já construídos, para esse mesmo evento é usado o sign_up_general_info_step_advanced para medir o acesso ao step 2 do checkout. A tabela checkout_step_2_advanced é menos atualizada, embora reflita o mesmo evento.',
    relevantUserActivity: true,
    componentName: 'step-advanced-component',
    additionalProperties,
    useTitleCaseEventText: true,
  });
}

export function trackSignUpAccountCreationFailed(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CHECKOUT_SIGN_UP_ACCOUNT_CREATION_FAILED,
    category: ERROR_TECHNICAL_CATEGORY,
    eventMeaning:
      '[Checkout] Clicou em avançar no step 1 e tomou erro, sem conseguir avançar para o Step 2',
    relevantUserActivity: false,
    componentName: 'creation-failed-component',
    additionalProperties,
    useTitleCaseEventText: true,
  });
}

export function trackCapicandiaOnboardingAnswered(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CAPICANDIA_ONBOARDING_ANSWERED,
    category: ONBOARDING_ACTIVATION_CATEGORY,
    eventMeaning:
      '[Capicandia] Resposta do usuário no gate de onboarding do /cadastro ("Você já tem conta na Jusfy?")',
    relevantUserActivity: true,
    componentName: 'capicandia-onboarding-component',
    additionalProperties,
  });
}

export function trackCapicandiaOnboardingStatusChecked(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CAPICANDIA_ONBOARDING_STATUS_CHECKED,
    category: ONBOARDING_ACTIVATION_CATEGORY,
    eventMeaning:
      '[Capicandia] Resultado da verificação de status da conta (NOT_FOUND/ACTIVE/CANCELED) no gate de onboarding do /cadastro',
    relevantUserActivity: false,
    componentName: 'capicandia-onboarding-component',
    additionalProperties,
  });
}

export function trackCapicandiaOnboardingPlanSelected(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CAPICANDIA_ONBOARDING_PLAN_SELECTED,
    category: ONBOARDING_ACTIVATION_CATEGORY,
    eventMeaning:
      '[Capicandia] Plano escolhido na reativação de conta cancelada dentro do gate de onboarding do /cadastro',
    relevantUserActivity: true,
    componentName: 'capicandia-onboarding-component',
    additionalProperties,
  });
}

export function trackCapicandiaDownsellRequested(
  additionalProperties?: AdditionalProperties,
): void {
  trackEvent({
    event: EVENTS.CAPICANDIA_DOWNSELL_REQUESTED,
    category: OTHER_CATEGORY,
    eventMeaning:
      '[Capicandia] Usuário com plano ativo solicitou contato de especialista para migrar para Capicand-IA + Starter (downsell) no checkout do addon',
    relevantUserActivity: true,
    componentName: 'capicandia-checkout-component',
    additionalProperties,
  });
}
